import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Search,
  Edit,
  Trash2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { Website } from "../WebsiteManagement";
import { toast } from "sonner";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { CategoryCombobox } from "../website-suggest/CategoryCombobox";
import { RegionSelector } from "../website-suggest/RegionSelector";
import {
  AddressAutocomplete,
  type AddressAutocompleteStatus,
} from "../website-form/AddressAutocomplete";
import {
  checkSuggestionUrlBoth,
  guessRegionByZipCode,
  suggestCategoryByAi,
} from "@/services/websiteSuggestion.service";
import { buildCategoryBreadcrumbLabel } from "@/utils/categoryLabel";
import {
  getAddressAutocompleteEnabled,
  getAiCategorySuggestionEnabled,
} from "@/services/publicSystemSettings.service";
import {
  logGeocodingEvent,
  type GeocodingConfidence,
  type GeocodingRegion,
  type GeocodingRegionType,
  type GeocodingSuggestion,
} from "@/services/geocoding.service";
import {
  getManagedWebsitesPaged,
  type ManagedWebsite,
} from "@/services/website.service";
import { Skeleton } from "../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const requiredAsteriskStyle = { color: "var(--primary)" } as const;
const renderRequiredLabel = (label: string) => {
  const starIndex = label.indexOf("*");
  if (starIndex === -1) return label;
  const before = label.slice(0, starIndex);
  const after = label.slice(starIndex + 1);
  return (
    <>
      {before}
      <span style={requiredAsteriskStyle}>*</span>
      {after}
    </>
  );
};

// Backend-aligned max length constants
const MAX_TITLE_LEN = 200;
const MAX_DESCRIPTION_LEN = 500;
const MAX_URL_LEN = 512;
const MAX_ADDRESS_LEN = 512;
const MAX_ZIP_LEN = 5;

// Normalize URL like backend:
// - trim
// - ensure scheme (https default)
// - lowercase host + remove leading "www."
// - keep path + query
// - drop fragment (#...)
const normalizeUrlKeepPathQuery = (input: string) => {
  const raw = input.trim();
  if (!raw) return raw;

  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  let u: URL;
  try {
    u = new URL(withScheme);
  } catch {
    return withScheme.split("#")[0].trim();
  }

  let host = (u.hostname || "").toLowerCase();
  if (host.startsWith("www.")) host = host.slice(4);

  const path = u.pathname || "";
  const query = u.search || "";
  return `https://${host}${path}${query}`;
};

type UrlErrorKey =
  | "websiteSuggest.errorUrlExists"
  | "websiteSuggest.errorUrlVerifyFailed";

interface ManageWebsitesPanelProps {
  onEditWebsite: (id: string, updates: Partial<Website>) => void;
  onDeleteWebsite: (id: string) => void;
}

type WebsiteEditFormData = {
  id: string;
  title: string;
  url: string;
  description: string;
  address?: string | null;
  zipCode: string;
  category1Id: string | null;
  category1Name: string;
  categoryPathIds: string[];
  categoryPathNames: string[];
  category1Depth: number | null;
  regionId: string | null;
  regionName: string;
  regionPathIds: string[];
  regionPathNames: string[];
};

const toEditFormData = (website: ManagedWebsite): WebsiteEditFormData => ({
  id: website.id,
  title: website.title,
  url: website.url,
  description: website.description,
  address: website.address ?? "",
  zipCode: website.zipCode ?? "",
  category1Id: website.category1Id ?? null,
  category1Name: website.category1Name ?? "",
  categoryPathIds: website.categoryPathIds ?? [],
  categoryPathNames: website.categoryPathNames ?? [],
  category1Depth: null,
  regionId: website.regionId ?? null,
  regionName: website.region ?? "",
  regionPathIds: website.regionPathIds ?? [],
  regionPathNames: website.regionPathNames ?? [],
});

export function ManageWebsitesPanel({
  onEditWebsite,
  onDeleteWebsite,
}: ManageWebsitesPanelProps) {
  const { t, language } = useApp();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const errorTextStyle = {
    color: "color-mix(in srgb, var(--accent) 82%, var(--foreground))",
  } as const;
  const overlayStyle = {
    backgroundColor: "color-mix(in srgb, var(--foreground) 50%, transparent)",
  } as const;
  const [editingWebsite, setEditingWebsite] =
    useState<WebsiteEditFormData | null>(null);
  const [websites, setWebsites] = useState<ManagedWebsite[]>([]);
  const [loadingWebsites, setLoadingWebsites] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [queryInput, setQueryInput] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("relevance");
  const [dateFilter, setDateFilter] = useState("all");
  const [openCategory1, setOpenCategory1] = useState(false);
  const [openRegion, setOpenRegion] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [reloadSeq, setReloadSeq] = useState(0);

  const [autoConfidence, setAutoConfidence] =
    useState<GeocodingConfidence | null>(null);
  const [autoStatus, setAutoStatus] =
    useState<AddressAutocompleteStatus>("idle");
  const [autoRegionType, setAutoRegionType] =
    useState<GeocodingRegionType | null>(null);
  const [addressAutocompleteEnabled, setAddressAutocompleteEnabled] =
    useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFeatureEnabled, setAiFeatureEnabled] = useState(true);

  const [fieldErrors, setFieldErrors] = useState({
    title: false,
    url: false,
    description: false,
  });
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlErrorKey, setUrlErrorKey] = useState<UrlErrorKey | null>(null);

  const [wasDescriptionTooLong, setWasDescriptionTooLong] = useState(false);
  const [wasTitleTooLong, setWasTitleTooLong] = useState(false);
  const [wasUrlTooLong, setWasUrlTooLong] = useState(false);
  const [wasAddressTooLong, setWasAddressTooLong] = useState(false);
  const [wasZipTooLong, setWasZipTooLong] = useState(false);

  const [autoRegionApplied, setAutoRegionApplied] = useState(false);

  const setEditingFormData = (
    next:
      | WebsiteEditFormData
      | ((prev: WebsiteEditFormData) => WebsiteEditFormData)
  ) => {
    setEditingWebsite((prev) => {
      if (!prev) return prev;
      return typeof next === "function" ? next(prev) : next;
    });
  };

  useEffect(() => {
    if (!editingWebsite) return;
    setAiLoading(false);
    setShowValidation(false);
    setFieldErrors({ title: false, url: false, description: false });
    setUrlErrorKey(null);
    setWasDescriptionTooLong(false);
    setWasTitleTooLong(false);
    setWasUrlTooLong(false);
    setWasAddressTooLong(false);
    setWasZipTooLong(false);
    setAutoRegionApplied(false);
    setAutoConfidence(null);
    setAutoStatus("idle");
    setAutoRegionType(null);
  }, [editingWebsite?.id]);

  useEffect(() => {
    let isMounted = true;
    const loadSetting = async () => {
      const enabled = await getAddressAutocompleteEnabled();
      if (isMounted) setAddressAutocompleteEnabled(enabled);
    };
    loadSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadAiSetting = async () => {
      const enabled = await getAiCategorySuggestionEnabled();
      if (isMounted) setAiFeatureEnabled(enabled);
    };
    loadAiSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (aiFeatureEnabled) return;
    setAiLoading(false);
  }, [aiFeatureEnabled]);



  useEffect(() => {
    if (!editingWebsite) return;

    setEditingFormData((prev) => ({
      ...prev,
      category1Id: null,
      category1Name: "",
      category1Depth: null,
      categoryPathIds: [],
      categoryPathNames: [],
    }));

    setAiLoading(false);
    setOpenCategory1(false);
  }, [language]);

  const clearRegionSelection = useCallback(() => {
    setEditingFormData((prev) => ({
      ...prev,
      regionId: null,
      regionName: "",
      regionPathIds: [],
      regionPathNames: [],
    }));
  }, [setEditingFormData]);

  useEffect(() => {
    if (!editingWebsite) return;

    const z = (editingWebsite.zipCode ?? "").trim();
    const hasRegionSelection =
      Boolean(editingWebsite.regionId) ||
      Boolean(editingWebsite.regionName) ||
      (editingWebsite.regionPathIds?.length ?? 0) > 0 ||
      (editingWebsite.regionPathNames?.length ?? 0) > 0;

    if (hasRegionSelection && !autoRegionApplied) {
      return;
    }

    if (!z) {
      setAutoRegionApplied(false);
      return;
    }

    if (!/^\d{5}$/.test(z)) {
      if (autoRegionApplied) {
        clearRegionSelection();
        setAutoRegionApplied(false);
      }
      return;
    }

    const timer = window.setTimeout(async () => {
      const res = await guessRegionByZipCode(z);

      if (!res.ok) return;

      if (!res.regionId) {
        if (autoRegionApplied) {
          clearRegionSelection();
          setAutoRegionApplied(false);
        }
        return;
      }

      setEditingFormData((prev) => {
        const userHasManualRegion = Boolean(prev.regionId) && !autoRegionApplied;
        if (userHasManualRegion) return prev;

        const name =
          res.regionName && res.regionName.trim() ? res.regionName.trim() : "";

        return {
          ...prev,
          regionId: res.regionId,
          regionName: name,
          regionPathIds: [res.regionId],
          regionPathNames: name ? [name] : [],
        };
      });

      setAutoRegionApplied(true);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [
    editingWebsite?.zipCode,
    editingWebsite?.regionId,
    editingWebsite?.regionName,
    editingWebsite?.regionPathIds,
    editingWebsite?.regionPathNames,
    autoRegionApplied,
    clearRegionSelection,
    setEditingFormData,
  ]);

  const handleAutoSelect = useCallback(
    (suggestion: GeocodingSuggestion) => {
      const resolveRegionType = (region: GeocodingRegion | null) => {
        if (!region) return null;
        if (region.type) return region.type;
        const depth = Math.max(region.pathIds.length, region.pathNames.length);
        if (depth >= 4) return "POSTAL_CODE";
        if (depth === 3) return "DISTRICT";
        if (depth === 2) return "ADMINISTRATIVE_DISTRICT";
        if (depth === 1 || region.id) return "STATE";
        return null;
      };

      setAutoStatus("idle");
      setAutoConfidence(suggestion.confidence);
      setAutoRegionApplied(false);
      setAutoRegionType(resolveRegionType(suggestion.region));
      void logGeocodingEvent({
        event: "resolution_selected",
        confidence: suggestion.confidence,
      });

      setEditingFormData((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          address: suggestion.label,
          zipCode: suggestion.postalCode ?? prev.zipCode,
        };
        if (suggestion.region) {
          const pathIds =
            suggestion.region.pathIds.length > 0
              ? suggestion.region.pathIds
              : [suggestion.region.id];
          const pathNames =
            suggestion.region.pathNames.length > 0
              ? suggestion.region.pathNames
              : [suggestion.region.name];

          next.regionId = suggestion.region.id;
          next.regionName = suggestion.region.name;
          next.regionPathIds = pathIds;
          next.regionPathNames = pathNames;
        }
        return next;
      });

      if (!suggestion.region) {
        setAutoStatus("invalid_response");
      }
    },
    [setEditingFormData]
  );

  const lastLoggedStatusRef = useRef<AddressAutocompleteStatus>("idle");
  useEffect(() => {
    if (!addressAutocompleteEnabled) return;
    if (autoStatus === "idle" || autoStatus === "no_results") {
      lastLoggedStatusRef.current = autoStatus;
      return;
    }
    if (lastLoggedStatusRef.current === autoStatus) return;

    lastLoggedStatusRef.current = autoStatus;
    void logGeocodingEvent({
      event: "autocomplete_status",
      reason: autoStatus,
    });
  }, [addressAutocompleteEnabled, autoStatus]);

  const pageSizeNum = Math.max(1, Math.min(100, parseInt(pageSize, 10) || 10));
  const currentPage = Math.max(1, totalPages > 0 ? Math.min(page, totalPages) : page);

  const loadManagedWebsites = useCallback(
    async (signal?: AbortSignal) => {
      if (authLoading || !isAuthenticated) {
        return;
      }
      setLoadingWebsites(true);
      setLoadingError(null);
      try {
        const response = await getManagedWebsitesPaged({
          page: Math.max(0, currentPage - 1),
          size: pageSizeNum,
          query: committedQuery,
          sortBy,
          dateFilter,
          lang: language,
        }, { signal });

        if (signal?.aborted) return;

        setWebsites(response.items);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);

        if (response.totalPages > 0 && currentPage > response.totalPages) {
          setPage(response.totalPages);
        }
      } catch (error) {
        if (signal?.aborted) return;
        console.error("Failed to load managed websites", error);
        setWebsites([]);
        setTotalElements(0);
        setTotalPages(0);
        setLoadingError(t("common.genericError"));
      } finally {
        if (!signal?.aborted) {
          setLoadingWebsites(false);
        }
      }
    },
    [
      authLoading,
      isAuthenticated,
      committedQuery,
      currentPage,
      dateFilter,
      language,
      pageSizeNum,
      sortBy,
      t,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadManagedWebsites(controller.signal);
    return () => {
      controller.abort();
    };
  }, [loadManagedWebsites, reloadSeq]);

  const titleTooLong = editingWebsite
    ? editingWebsite.title.length > MAX_TITLE_LEN
    : false;
  const urlTooLong = editingWebsite
    ? editingWebsite.url.length > MAX_URL_LEN
    : false;
  const descriptionTooLong = editingWebsite
    ? editingWebsite.description.length > MAX_DESCRIPTION_LEN
    : false;
  const addressTooLong = editingWebsite
    ? (editingWebsite.address ?? "").length > MAX_ADDRESS_LEN
    : false;
  const zipTooLong = editingWebsite
    ? (editingWebsite.zipCode ?? "").length > MAX_ZIP_LEN
    : false;
  const hasValidZip = editingWebsite
    ? /^\d{5}$/.test((editingWebsite.zipCode ?? "").trim())
    : false;
  const categoryMissing =
    showValidation && editingWebsite ? !editingWebsite.category1Id : false;
  const regionMissing =
    showValidation && editingWebsite
      ? !editingWebsite.regionId && !hasValidZip
      : false;
  const requiredFieldsFilled = editingWebsite
    ? Boolean(editingWebsite.title.trim()) &&
      Boolean(editingWebsite.url.trim()) &&
      Boolean(editingWebsite.description.trim()) &&
      Boolean(editingWebsite.category1Id) &&
      (Boolean(editingWebsite.regionId) || hasValidZip)
    : false;
  const aiMissingRequiredFields = editingWebsite
    ? !editingWebsite.title.trim() ||
      !editingWebsite.url.trim() ||
      !editingWebsite.description.trim()
    : true;
  const saveDisabled = isSubmitting || !requiredFieldsFilled;
  const showSaveTooltip = !requiredFieldsFilled;

  const titleInvalid = (showValidation && fieldErrors.title) || titleTooLong;
  const urlInvalid =
    (showValidation && fieldErrors.url) || urlErrorKey !== null || urlTooLong;
  const descriptionInvalid =
    (showValidation && fieldErrors.description) || descriptionTooLong;
  const zipInvalid = zipTooLong;

  const confidenceLabel =
    autoConfidence === "EXACT"
      ? t("websiteSuggest.addressConfidenceExact")
      : autoConfidence === "AMBIGUOUS"
        ? t("websiteSuggest.addressConfidenceAmbiguous")
        : autoConfidence === "PARTIAL"
          ? autoRegionType === "STATE"
            ? t("websiteSuggest.addressConfidenceState")
            : autoRegionType === "ADMINISTRATIVE_DISTRICT"
              ? t("websiteSuggest.addressConfidenceAdminDistrict")
              : autoRegionType === "DISTRICT"
                ? t("websiteSuggest.addressConfidenceDistrict")
                : autoRegionType === "POSTAL_CODE"
                  ? t("websiteSuggest.addressConfidenceExact")
                  : t("websiteSuggest.addressConfidencePartial")
          : null;

  const handleAiSuggest = async () => {
    if (!editingWebsite) return;

    const titleEmpty = !editingWebsite.title.trim();
    const urlEmpty = !editingWebsite.url.trim();
    const descriptionEmpty = !editingWebsite.description.trim();

    if (titleEmpty || urlEmpty || descriptionEmpty) {
      toast.error(t("websiteSuggest.aiNeedTitleAndDescription"));
      return;
    }

    if (editingWebsite.title.length > MAX_TITLE_LEN) {
      toast.error(
        t("websiteSuggest.errorTitleTooLong").replace("{max}", String(MAX_TITLE_LEN))
      );
      return;
    }
    if (editingWebsite.description.length > MAX_DESCRIPTION_LEN) {
      toast.error(
        t("websiteSuggest.errorDescriptionTooLong").replace(
          "{max}",
          String(MAX_DESCRIPTION_LEN)
        )
      );
      return;
    }

    if (aiLoading) return;

    setAiLoading(true);
    try {
      const res = await suggestCategoryByAi({
        title: editingWebsite.title.trim(),
        description: editingWebsite.description.trim(),
        lang: language === "de" ? "de" : "en",
      });

      if (!res.ok) {
        switch (res.kind) {
          case "timeout":
            toast.error(t("websiteSuggest.aiTimeout"));
            break;
          case "bad_gateway":
            toast.error(t("websiteSuggest.aiBadGateway"));
            break;
          default:
            toast.error(t("websiteSuggest.aiFailed"));
            break;
        }
        return;
      }

      const data = res.data;

      const pathIds = Array.isArray(data.categoryPathIds) ? data.categoryPathIds : [];
      const pathNames = Array.isArray(data.categoryPathNames) ? data.categoryPathNames : [];

      const leafId = pathIds.length > 0 ? pathIds[pathIds.length - 1] : data.categoryId;
      const leafName = pathNames.length > 0 ? pathNames[pathNames.length - 1] : data.categoryName;

      setEditingFormData((prev) => ({
        ...prev,
        category1Id: leafId,
        category1Name: leafName,
        category1Depth: typeof data.depth === "number" ? data.depth : null,
        categoryPathIds: pathIds,
        categoryPathNames: pathNames,
      }));

      setOpenCategory1(false);

      toast.success(t("websiteSuggest.aiApplied"));
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingWebsite) return;

    setShowValidation(true);
    setUrlErrorKey(null);

    const titleEmpty = !editingWebsite.title.trim();
    const urlEmpty = !editingWebsite.url.trim();
    const descriptionEmpty = !editingWebsite.description.trim();

    setFieldErrors({
      title: titleEmpty,
      url: urlEmpty,
      description: descriptionEmpty,
    });

    if (titleEmpty || urlEmpty || descriptionEmpty) {
      toast.error(t("websiteSuggest.fillAllFields"));
      const firstErrorId = titleEmpty
        ? "edit-title"
        : urlEmpty
        ? "edit-url"
        : "edit-description";
      const el = document.getElementById(firstErrorId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (editingWebsite.title.length > MAX_TITLE_LEN) {
      toast.error(t("websiteSuggest.errorTitleTooLong").replace("{max}", String(MAX_TITLE_LEN)));
      document.getElementById("edit-title")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (editingWebsite.url.length > MAX_URL_LEN) {
      toast.error(t("websiteSuggest.errorUrlTooLong").replace("{max}", String(MAX_URL_LEN)));
      document.getElementById("edit-url")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (editingWebsite.description.length > MAX_DESCRIPTION_LEN) {
      toast.error(
        t("websiteSuggest.errorDescriptionTooLong").replace("{max}", String(MAX_DESCRIPTION_LEN))
      );
      document
        .getElementById("edit-description")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if ((editingWebsite.address ?? "").length > MAX_ADDRESS_LEN) {
      toast.error(t("websiteSuggest.errorAddressTooLong").replace("{max}", String(MAX_ADDRESS_LEN)));
      document
        .getElementById("edit-address")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if ((editingWebsite.zipCode ?? "").length > MAX_ZIP_LEN) {
      toast.error(t("websiteSuggest.errorZipTooLong").replace("{max}", String(MAX_ZIP_LEN)));
      document.getElementById("zipCode")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const missingCategory = !editingWebsite.category1Id;
    const missingRegion =
      !editingWebsite.regionId && !/^\d{5}$/.test(editingWebsite.zipCode.trim());

    if (missingCategory || missingRegion) {
      if (missingCategory) {
        toast.error(t("websiteManagement.categoryRequiredError"));
      }
      if (missingRegion) {
        toast.error(t("websiteManagement.regionRequiredError"));
      }
      const targetId = missingCategory ? "edit-category" : "edit-region";
      document.getElementById(targetId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const normalizedUrl = normalizeUrlKeepPathQuery(editingWebsite.url);

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const current = websites.find((w) => w.id === editingWebsite.id);
      const currentUrl = current ? normalizeUrlKeepPathQuery(current.url) : "";

      if (normalizedUrl && normalizedUrl !== currentUrl) {
        const check = await checkSuggestionUrlBoth(normalizedUrl);

        if (!check.ok) {
          setFieldErrors((prev) => ({ ...prev, url: true }));
          setUrlErrorKey("websiteSuggest.errorUrlVerifyFailed");
          toast.error(t("websiteSuggest.errorUrlVerifyFailed"));
          return;
        }

        if (check.exists) {
          setFieldErrors((prev) => ({ ...prev, url: true }));
          setUrlErrorKey("websiteSuggest.errorUrlExists");
          toast.error(t("websiteSuggest.errorUrlExists"));
          document.getElementById("edit-url")?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }

      await onEditWebsite(editingWebsite.id, {
        title: editingWebsite.title.trim(),
        url: normalizedUrl,
        description: editingWebsite.description.trim(),
        address: editingWebsite.address ?? null,
        zipCode: editingWebsite.zipCode,
        category1Id: editingWebsite.category1Id ?? null,
        category1Name: editingWebsite.category1Name,
        categoryPathIds: editingWebsite.categoryPathIds,
        categoryPathNames: editingWebsite.categoryPathNames,
        regionId: editingWebsite.regionId ?? null,
        region: editingWebsite.regionName,
        regionPathIds: editingWebsite.regionPathIds,
        regionPathNames: editingWebsite.regionPathNames,
      });
      toast.success(t("websiteManagement.websiteUpdated"));
      setEditingWebsite(null);
      setReloadSeq((prev) => prev + 1);
    } catch {
      toast.error(t("common.genericError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (website: ManagedWebsite) => {
    try {
      await onDeleteWebsite(website.id);
      toast.success(
        t("websiteManagement.websiteDeleted").replace("{title}", website.title)
      );
      setReloadSeq((prev) => prev + 1);
    } catch {
      toast.error(t("common.genericError"));
    }
  };

  const handleSearchSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    const trimmed = queryInput.trim();
    if (trimmed.length > 200) {
      setSearchError(
        t("common.searchTooLong") ||
          "Search term must be at most 200 characters."
      );
      return;
    }
    setSearchError(null);
    setCommittedQuery(trimmed);
    setPage(1);
  };

  const totalPagesDisplay = Math.max(1, totalPages);

  const rangeStart = totalElements > 0 ? (currentPage - 1) * pageSizeNum + 1 : 0;
  const rangeEnd = totalElements > 0 ? Math.min(currentPage * pageSizeNum, totalElements) : 0;
  const summaryTemplate =
    t("categories.resultsSummary") || "{{start}}-{{end}} / {{count}}";
  const resultsSummary = summaryTemplate
    .replace(/\{\{start\}\}/g, String(rangeStart))
    .replace(/\{\{end\}\}/g, String(rangeEnd))
    .replace(/\{\{count\}\}/g, String(totalElements));

  const renderWebsiteSkeletons = (count: number) => (
    <div className="space-y-4">
      {Array.from({ length: Math.max(1, count) }).map((_, idx) => (
        <Card key={`manage-website-skeleton-${idx}`} className="stat-card">
          <CardContent className="pt-6 pb-4">
            <div className="space-y-3">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-6 w-44 rounded-full" />
                <Skeleton className="h-6 w-56 rounded-full" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-9 w-24 rounded-md" />
                <Skeleton className="h-9 w-24 rounded-md" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <>
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="card-title-text" style={primaryTextStyle}>
            {t("websiteManagement.manageWebsitesTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("websiteManagement.manageWebsitesDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            onSubmit={handleSearchSubmit}
            className="flex flex-col sm:flex-row gap-2"
          >
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={iconStyle}
              />
              <Input
                placeholder={t("websiteManagement.searchPlaceholder")}
                className="pl-9"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                maxLength={200}
                aria-invalid={searchError ? true : undefined}
              />
            </div>
            <Button type="submit">{t("common.search")}</Button>
          </form>

          {searchError && (
            <p className="text-sm font-semibold" style={errorTextStyle}>
              {searchError}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label style={secondaryTextStyle}>{t("categories.sortBy")}</Label>
              <Select
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("categories.sortBy")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">
                    {t("categories.sortRelevance")}
                  </SelectItem>
                  <SelectItem value="date-new">
                    {t("categories.sortDateNew")}
                  </SelectItem>
                  <SelectItem value="date-old">
                    {t("categories.sortDateOld")}
                  </SelectItem>
                  <SelectItem value="title-asc">
                    {t("categories.sortTitleAsc")}
                  </SelectItem>
                  <SelectItem value="title-desc">
                    {t("categories.sortTitleDesc")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label style={secondaryTextStyle}>
                {t("categories.filterTime")}
              </Label>
              <Select
                value={dateFilter}
                onValueChange={(value) => {
                  setDateFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("categories.filterTime")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("categories.allPeriods")}</SelectItem>
                  <SelectItem value="week">{t("categories.lastWeek")}</SelectItem>
                  <SelectItem value="month">{t("categories.lastMonth")}</SelectItem>
                  <SelectItem value="year">{t("categories.lastYear")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label style={secondaryTextStyle}>
                {t("categories.resultsPerPage")}
              </Label>
              <Select
                value={pageSize}
                onValueChange={(value) => {
                  setPageSize(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="10" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs" style={secondaryTextStyle}>
              {loadingWebsites ? t("websiteSuggest.loading") : resultsSummary}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage <= 1 || loadingWebsites}
                    aria-label={t("categories.paginationPrevious")}
                  >
                    <ChevronLeft className="w-4 h-4" style={iconStyle} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t("categories.paginationPrevious")}
                </TooltipContent>
              </Tooltip>
              <div className="flex items-center gap-1">
                {renderPaginationButtons(currentPage, totalPagesDisplay, setPage)}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setPage((prev) => Math.min(totalPagesDisplay, prev + 1))}
                    disabled={currentPage >= totalPagesDisplay || loadingWebsites}
                    aria-label={t("common.next")}
                  >
                    <ChevronRight className="w-4 h-4" style={iconStyle} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("common.next")}</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {loadingError && (
            <Card>
              <CardContent className="py-4 text-sm font-semibold" style={errorTextStyle}>
                {loadingError}
              </CardContent>
            </Card>
          )}

          {loadingWebsites ? (
            renderWebsiteSkeletons(pageSizeNum)
          ) : websites.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-secondary" style={secondaryTextStyle}>
                {t("common.noResults")}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {websites.map((website) => {
                const categoryLabel = buildCategoryBreadcrumbLabel(
                  website.categoryPathNames,
                  website.category1Name
                );
                const locationLabel =
                  website.regionPathNames && website.regionPathNames.length > 0
                    ? website.regionPathNames.join(" > ")
                    : website.region && website.region.trim().length > 0
                    ? website.region
                    : website.zipCode && website.zipCode.trim().length > 0
                    ? website.zipCode
                    : "—";

                return (
                  <Card key={website.id} className="stat-card">
                    <CardContent className="pt-6 pb-4">
                      <div className="flex flex-col gap-3">
                        <div>
                          <p style={primaryTextStyle}>{website.title}</p>
                          <p className="text-xs break-all" style={secondaryTextStyle}>
                            {website.url}
                          </p>
                        </div>
                        <p className="text-sm" style={primaryTextStyle}>
                          {website.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          {categoryLabel && (
                            <Badge variant="secondary" className="whitespace-normal break-words text-left max-w-full">
                              {t("websiteManagement.tableCategory")}: {categoryLabel}
                            </Badge>
                          )}
                          <Badge variant="secondary" className="whitespace-normal break-words text-left max-w-full">
                            {t("websiteManagement.tableLocation")}: {locationLabel}
                          </Badge>
                        </div>
                        <p className="text-xs" style={secondaryTextStyle}>
                          {t("websiteManagement.tableAdded")}: {website.addedAt
                            ? new Date(website.addedAt).toLocaleDateString("de-DE")
                            : "?"}
                          {website.addedBy ? ` · ${website.addedBy}` : ""}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button
                            variant="outline"
                            className="gap-2 w-full sm:w-auto"
                            onClick={() => setEditingWebsite(toEditFormData(website))}
                          >
                            <Edit className="w-4 h-4" />
                            {t("websiteManagement.editButton")}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                className="gap-2 w-full sm:w-auto"
                              >
                                <Trash2 className="w-4 h-4" />
                                {t("websiteManagement.deleteButton")}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t("websiteManagement.deleteConfirmTitle")}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("websiteManagement.deleteConfirmDescription").replace(
                                    "{title}",
                                    website.title
                                  )}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => {
                                    void handleDelete(website);
                                  }}
                                >
                                  {t("common.delete")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs" style={secondaryTextStyle}>
              {t("common.page")} {currentPage} / {totalPagesDisplay}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage <= 1 || loadingWebsites}
                    aria-label={t("categories.paginationPrevious")}
                  >
                    <ChevronLeft className="w-4 h-4" style={iconStyle} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t("categories.paginationPrevious")}
                </TooltipContent>
              </Tooltip>
              <div className="flex items-center gap-1">
                {renderPaginationButtons(currentPage, totalPagesDisplay, setPage)}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setPage((prev) => Math.min(totalPagesDisplay, prev + 1))}
                    disabled={currentPage >= totalPagesDisplay || loadingWebsites}
                    aria-label={t("common.next")}
                  >
                    <ChevronRight className="w-4 h-4" style={iconStyle} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("common.next")}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardContent>
      </Card>

      {editingWebsite && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50"
          style={overlayStyle}
        >
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto stat-card">
            <CardHeader>
              <CardTitle className="card-title-text" style={primaryTextStyle}>
                {t("websiteManagement.editWebsiteTitle")}
              </CardTitle>
              <CardDescription
                className="card-description-text"
                style={secondaryTextStyle}
              >
                {t("websiteManagement.editWebsiteDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label style={primaryTextStyle}>
                    {renderRequiredLabel(t("websiteManagement.titleLabel"))}
                  </Label>
                  <Input
                    id="edit-title"
                    value={editingWebsite.title}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditingFormData({
                        ...editingWebsite,
                        title: value,
                      });

                      const tooLong = value.length > MAX_TITLE_LEN;
                      if (tooLong && !wasTitleTooLong) {
                        toast.error(
                          t("websiteSuggest.errorTitleTooLong").replace(
                            "{max}",
                            String(MAX_TITLE_LEN)
                          )
                        );
                      }
                      setWasTitleTooLong(tooLong);

                      if (showValidation) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          title: !value.trim(),
                        }));
                      }
                    }}
                    aria-invalid={titleInvalid ? true : undefined}
                  />

                  {showValidation && fieldErrors.title && (
                    <p className="text-xs font-semibold" style={errorTextStyle}>
                      {t("websiteSuggest.errorTitleRequired")}
                    </p>
                  )}

                  {titleTooLong && (
                    <p className="text-xs font-semibold" style={errorTextStyle}>
                      {t("websiteSuggest.errorTitleTooLong").replace(
                        "{max}",
                        String(MAX_TITLE_LEN)
                      )}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label style={primaryTextStyle}>
                    {renderRequiredLabel(t("websiteManagement.urlLabel"))}
                  </Label>
                  <Input
                    id="edit-url"
                    value={editingWebsite.url}
                    placeholder={t("websiteSuggest.urlPlaceholder")}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditingFormData({
                        ...editingWebsite,
                        url: value,
                      });
                      setUrlErrorKey(null);

                      const tooLong = value.length > MAX_URL_LEN;
                      if (tooLong && !wasUrlTooLong) {
                        toast.error(
                          t("websiteSuggest.errorUrlTooLong").replace(
                            "{max}",
                            String(MAX_URL_LEN)
                          )
                        );
                      }
                      setWasUrlTooLong(tooLong);

                      if (showValidation) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          url: !value.trim(),
                        }));
                      }
                    }}
                    aria-invalid={urlInvalid ? true : undefined}
                  />

                  {urlInvalid && (
                    <p className="text-xs font-semibold" style={errorTextStyle}>
                      {urlTooLong
                        ? t("websiteSuggest.errorUrlTooLong").replace(
                            "{max}",
                            String(MAX_URL_LEN)
                          )
                        : urlErrorKey
                        ? t(urlErrorKey)
                        : t("websiteSuggest.errorUrlRequired")}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label style={primaryTextStyle}>
                  {renderRequiredLabel(t("websiteManagement.descriptionLabel"))}
                </Label>
                <Textarea
                  id="edit-description"
                  rows={4}
                  value={editingWebsite.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingFormData({
                      ...editingWebsite,
                      description: value,
                    });

                    if (showValidation) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        description: !value.trim(),
                      }));
                    }

                    const tooLong = value.length > MAX_DESCRIPTION_LEN;
                    if (tooLong && !wasDescriptionTooLong) {
                      toast.error(
                        t("websiteSuggest.errorDescriptionTooLong").replace(
                          "{max}",
                          String(MAX_DESCRIPTION_LEN)
                        )
                      );
                    }
                    setWasDescriptionTooLong(tooLong);
                  }}
                  aria-invalid={descriptionInvalid ? true : undefined}
                />

                {showValidation && fieldErrors.description && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorDescriptionRequired")}
                  </p>
                )}

                {descriptionTooLong && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorDescriptionTooLong").replace(
                      "{max}",
                      String(MAX_DESCRIPTION_LEN)
                    )}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-address" style={primaryTextStyle}>
                  {t("websiteSuggest.addressLabel")}
                </Label>
                <AddressAutocomplete
                  inputId="edit-address"
                  value={editingWebsite.address ?? ""}
                  onChange={(next) => {
                    setEditingWebsite({
                      ...editingWebsite,
                      address: next,
                    });
                    setAutoConfidence(null);
                    if (addressAutocompleteEnabled) {
                      setAutoStatus("idle");
                    }
                    setAutoRegionType(null);

                    const tooLong = next.length > MAX_ADDRESS_LEN;
                    if (tooLong && !wasAddressTooLong) {
                      toast.error(
                        t("websiteSuggest.errorAddressTooLong").replace(
                          "{max}",
                          String(MAX_ADDRESS_LEN)
                        )
                      );
                    }
                    setWasAddressTooLong(tooLong);
                  }}
                  onSelect={handleAutoSelect}
                  onStatusChange={setAutoStatus}
                  t={t}
                  suggestionsEnabled={addressAutocompleteEnabled}
                />

                {addressTooLong && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorAddressTooLong").replace(
                      "{max}",
                      String(MAX_ADDRESS_LEN)
                    )}
                  </p>
                )}

                {!addressAutocompleteEnabled && (
                  <p className="text-xs text-secondary">
                    {t("websiteSuggest.addressAutoDisabled")}
                  </p>
                )}

                {addressAutocompleteEnabled &&
                  (autoStatus === "unavailable" ||
                    autoStatus === "invalid_response") && (
                    <p className="text-xs text-secondary">
                      {t("websiteSuggest.addressAutoUnavailable")}
                    </p>
                  )}

                {addressAutocompleteEnabled && confidenceLabel && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" aria-live="polite">
                      {confidenceLabel}
                    </Badge>
                  </div>
                )}

                {addressAutocompleteEnabled && (
                  <p className="text-xs text-secondary">
                    {t("websiteSuggest.addressAutoHint")}
                  </p>
                )}
              </div>

              <div id="edit-region">
                <RegionSelector
                  formData={editingWebsite}
                  setFormData={setEditingFormData}
                  openRegion={openRegion}
                  setOpenRegion={setOpenRegion}
                  onZipCodeChange={(zip) => {
                    const cleaned = zip.replace(/\D/g, "").slice(0, 5);
                    setEditingWebsite((prev) =>
                      prev ? { ...prev, zipCode: cleaned } : prev
                    );
                    setAutoRegionApplied(false);

                    const tooLong = cleaned.length > MAX_ZIP_LEN;
                    if (tooLong && !wasZipTooLong) {
                      toast.error(
                        t("websiteSuggest.errorZipTooLong").replace(
                          "{max}",
                          String(MAX_ZIP_LEN)
                        )
                      );
                    }
                    setWasZipTooLong(tooLong);
                  }}
                  t={t}
                  labels={{
                    zipLabel: t("websiteManagement.zipCodeLabel"),
                    zipPlaceholder: t("websiteManagement.zipCodePlaceholder"),
                    zipHint: t("websiteManagement.zipCodeHint"),
                    regionLabel: t("websiteManagement.regionLabel"),
                    regionPlaceholder: t("websiteManagement.regionPlaceholder"),
                    regionHint: t("websiteManagement.regionHint"),
                  }}
                />

                {zipInvalid && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorZipTooLong").replace(
                      "{max}",
                      String(MAX_ZIP_LEN)
                    )}
                  </p>
                )}

                {regionMissing && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteManagement.regionRequiredError")}
                  </p>
                )}
              </div>

              <div
                id="edit-category"
                className="w-full sm:w-auto sm:flex-none min-w-0 sm:max-w-[280px]"
              >
                <CategoryCombobox
                  formData={editingWebsite}
                  setFormData={setEditingFormData}
                  openCategory1={openCategory1}
                  setOpenCategory1={setOpenCategory1}
                  t={t}
                  language={language}
                  disabled={isSubmitting}
                  labelText={t("websiteManagement.category")}
                  navigatorHintText={t("websiteSuggest.categoryNavigatorHint")}
                  emptyStateAction={
                    aiFeatureEnabled
                      ? {
                          label: aiLoading
                            ? t("websiteSuggest.aiSuggesting")
                            : t("websiteSuggest.aiSuggestButton"),
                          onClick: () => {
                            void handleAiSuggest();
                          },
                          disabled:
                            aiLoading ||
                            isSubmitting ||
                            aiMissingRequiredFields,
                          disabledTooltip: aiMissingRequiredFields
                            ? t("websiteSuggest.aiNeedTitleAndDescription")
                            : undefined,
                          icon: <Sparkles className="w-4 h-4" />,
                        }
                      : undefined
                  }
                />

                {categoryMissing && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteManagement.categoryRequiredError")}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {showSaveTooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="inline-flex"
                        tabIndex={0}
                        onClick={() => {
                          if (!editingWebsite) return;
                          setShowValidation(true);
                          setFieldErrors({
                            title: !editingWebsite.title.trim(),
                            url: !editingWebsite.url.trim(),
                            description: !editingWebsite.description.trim(),
                          });
                        }}
                      >
                        <Button
                          onClick={handleSave}
                          disabled={saveDisabled}
                        >
                          {t("websiteManagement.save")}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t("websiteManagement.fillAllFields")}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Button onClick={handleSave} disabled={saveDisabled}>
                    {t("websiteManagement.save")}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setEditingWebsite(null)}
                >
                  {t("websiteManagement.cancel")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

function renderPaginationButtons(
  currentPage: number,
  totalPages: number,
  setPage: (page: number) => void
) {
  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const maxVisible = 10;
  const visibleCount = Math.max(1, Math.min(maxVisible, safeTotalPages));
  const pages: number[] = [];

  if (safeTotalPages <= visibleCount) {
    for (let page = 1; page <= safeTotalPages; page += 1) {
      pages.push(page);
    }
  } else {
    const left = Math.floor((visibleCount - 1) / 2);
    const right = visibleCount - 1 - left;
    let start = safeCurrentPage - left;
    let end = safeCurrentPage + right;

    if (start < 1) {
      end += 1 - start;
      start = 1;
    }
    if (end > safeTotalPages) {
      start -= end - safeTotalPages;
      end = safeTotalPages;
    }

    start = Math.max(1, start);
    end = Math.min(safeTotalPages, end);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
  }

  return pages.map((page) => {
    return (
      <Button
        key={page}
        type="button"
        variant={currentPage === page ? "default" : "outline"}
        size="sm"
        onClick={() => setPage(page)}
        className={currentPage === page ? "bg-blue-600 hover:bg-blue-700" : ""}
      >
        {page}
      </Button>
    );
  });
}
